import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowDownCircle, ArrowUpCircle, History, SlidersHorizontal, Tag } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { useAuth } from '../../context/AuthContext';
import { useProducts } from '../../context/ProductsContext';
import {
  stockIn, stockOut, stockAdjust, STOCK_MOVE_ERRORS,
} from '../../api/inventory';
import { tombstoneTarget } from '../../utils/productMerge';
import StockMoveForm from './StockMoveForm';
import LotRepricePanel from './LotRepricePanel';
import LowStockPanel from './LowStockPanel';
import MovementHistoryPanel from './MovementHistoryPanel';
import styles from './Inventory.module.css';

const TABS = [
  { key: 'in', label: 'Stock In', icon: ArrowUpCircle },
  { key: 'out', label: 'Stock Out', icon: ArrowDownCircle },
  { key: 'adjust', label: 'Adjust', icon: SlidersHorizontal },
  { key: 'price', label: 'Shelf price', icon: Tag },
  { key: 'low', label: 'Low Stock', icon: AlertTriangle },
  { key: 'history', label: 'History', icon: History },
];

export default function Inventory() {
  const { user } = useAuth();
  const { products, movements, recordMovement, reloadProducts } = useProducts();
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedProductId = searchParams.get('productId') || '';
  const [tab, setTab] = useState('in');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const lowCount = products.filter((p) => p.stock <= p.lowStockThreshold).length;
  const activeProducts = useMemo(
    () => products.filter((p) => String(p.status || 'ACTIVE').toUpperCase() !== 'INACTIVE'),
    [products],
  );

  const handleMove = async ({
    productId, qty, reason, sellingPrice, mrp, unitCost, lotId, mode,
  }) => {
    if (!user?.idToken || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      if (mode === 'IN') {
        await stockIn(productId, {
          qty,
          selling_price: sellingPrice,
          mrp,
          unit_cost: unitCost,
          reason_code: 'PURCHASE',
          notes: reason,
        }, user.idToken);
        recordMovement(productId, 'IN', qty, reason, user.name || 'You', { syncStock: false });
      } else if (mode === 'OUT') {
        await stockOut(productId, {
          qty,
          lot_id: lotId,
          reason_code: 'DAMAGE',
          notes: reason || 'Stock out',
        }, user.idToken);
        recordMovement(productId, 'OUT', qty, reason, user.name || 'You', { syncStock: false });
      } else {
        await stockAdjust(productId, {
          lot_id: lotId,
          qty_delta: qty,
          notes: reason || 'cycle count',
          reason_code: 'CORRECTION',
        }, user.idToken);
        recordMovement(productId, 'ADJUST', qty, reason, user.name || 'You', { syncStock: false });
      }
      await reloadProducts();
    } catch (err) {
      const into = tombstoneTarget(err);
      if (into) {
        setSearchParams({ productId: into });
        setError('This product was merged. Stock moves apply to the surviving product.');
        return;
      }
      setError(STOCK_MOVE_ERRORS[err.message] || err.message || 'Could not save stock move.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Inventory"
        title="Stock Management"
        subtitle="Move stock in and out, correct counts, and keep an eye on what's about to run out."
      />

      <div className={styles.tabs}>
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} className={`${styles.tab} ${tab === key ? styles.tabActive : ''}`} onClick={() => { setTab(key); setError(''); }}>
            <Icon size={15} />
            {label}
            {key === 'low' && lowCount > 0 && <span className={styles.tabBadge}>{lowCount}</span>}
          </button>
        ))}
      </div>

      {tab === 'in' && (
        <StockMoveForm mode="IN" products={activeProducts} initialProductId={linkedProductId} onSubmit={handleMove} submitting={submitting} error={error} />
      )}
      {tab === 'out' && (
        <StockMoveForm mode="OUT" products={activeProducts} initialProductId={linkedProductId} onSubmit={handleMove} submitting={submitting} error={error} />
      )}
      {tab === 'adjust' && (
        <StockMoveForm mode="ADJUST" products={activeProducts} initialProductId={linkedProductId} onSubmit={handleMove} submitting={submitting} error={error} />
      )}
      {tab === 'price' && (
        <LotRepricePanel products={products} initialProductId={linkedProductId} />
      )}
      {tab === 'low' && (
        <LowStockPanel products={products} onStockIn={() => setTab('in')} />
      )}
      {tab === 'history' && (
        <MovementHistoryPanel movements={movements} />
      )}
    </div>
  );
}
